import { type FC, useEffect, useMemo, useState } from "react"

import { cn } from "@forento/ui/utilities/tailwind"

type Status = "queued" | "generating-outline" | "generating-content" | "generating-exam" | "finalizing"

const CREDIBILITY_MESSAGES = [
	"Learning from 6,000+ Forento academies",
	"Applying insights from the top 2% of creators",
	"Using patterns from successful courses",
	"Optimizing using real creator data",
]

const MESSAGES_BY_STATUS: Record<Status, string[]> = {
	queued: [
		"Analyzing your course concept",
		"Interpreting your expertise",
		"Understanding your audience",
		"Mapping your core knowledge",
		"Identifying valuable topics",
		"Extracting learning outcomes",
		"Clarifying your course promise",
		"Translating insights into curriculum",
		...CREDIBILITY_MESSAGES,
	],
	"generating-outline": [
		"Building your course framework",
		"Structuring the learning journey",
		"Designing your curriculum flow",
		"Organizing chapters for clarity",
		"Creating a scalable course outline",
		"Mapping lessons for progression",
		"Building a high-value structure",
		"Drafting your course blueprint",
		"Creating engaging chapter titles",
		...CREDIBILITY_MESSAGES,
	],
	"generating-content": [
		"Writing lesson summaries",
		"Drafting chapter introductions",
		"Converting expertise into lessons",
		"Creating learning modules",
		"Expanding chapters with insights",
		"Structuring teachable content",
		"Generating teaching points",
		"Building valuable lesson material",
		"Drafting your course content",
		"Creating actionable learning steps",
		"Analyzing high-performing courses",
		"Applying creator success patterns",
		"Improving perceived course value",
		"Enhancing lesson engagement",
		"Optimizing titles for conversion",
		"Applying proven course frameworks",
		"Polishing your course structure",
		"Refining chapter titles",
		"Optimizing your course flow",
		"Adding finishing touches",
		...CREDIBILITY_MESSAGES,
	],
	"generating-exam": [
		"Creating final exam questions",
		"Checking coverage across lessons",
		"Writing clear answer options",
		"Balancing question difficulty",
		"Marking the correct answers",
		"Reviewing question clarity",
		"Aligning the exam with course goals",
		"Preparing the final assessment",
		...CREDIBILITY_MESSAGES,
	],
	finalizing: [
		"Saving your generated course",
		"Organizing the final structure",
		"Connecting lessons and chapters",
		"Checking the generated content",
		"Preparing your draft course",
		"Polishing the final details",
		"Getting your course ready to open",
		"Wrapping up course generation",
	],
}

const ROTATION_INTERVAL_MS = 2_800
const FADE_DURATION_MS = 300

const shuffle = <T,>(items: readonly T[]): T[] => {
	const result = [...items]
	for (let i = result.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1))
		;[result[i], result[j]] = [result[j]!, result[i]!]
	}
	return result
}

type Props = { status: Status; className?: string }
const LoadingMessages: FC<Props> = ({ status, className }) => {
	const messages = useMemo(() => shuffle(MESSAGES_BY_STATUS[status]), [status])

	const [index, setIndex] = useState(0)
	const [isVisible, setIsVisible] = useState(true)

	useEffect(() => {
		setIndex(0)
		setIsVisible(true)
	}, [status])

	useEffect(() => {
		if (messages.length <= 1) return

		const rotate = setInterval(() => {
			setIsVisible(false)
			const fade = setTimeout(() => {
				setIndex(prev => (prev + 1) % messages.length)
				setIsVisible(true)
			}, FADE_DURATION_MS)
			return () => clearTimeout(fade)
		}, ROTATION_INTERVAL_MS)

		return () => clearInterval(rotate)
	}, [messages])

	const currentMessage = messages[index] ?? messages[0]
	if (currentMessage === undefined) return null

	return (
		<span
			className={cn("inline-block transition-opacity", className)}
			style={{ opacity: isVisible ? 1 : 0, transitionDuration: `${FADE_DURATION_MS}ms` }}
		>
			{currentMessage}
		</span>
	)
}

export default LoadingMessages
